/**
 * Mounts the shared ErrorSummary React component as an island on the
 * public event submission form (PESF).
 *
 * Listens for:
 *   - `validation:complete` on the form (client-side FormValidator errors)
 *   - DOMContentLoaded with pre-seeded server-side errors
 *
 * On error, renders ErrorSummary into `#event-edit-error-summary` and
 * manages focus per WCAG 2.4.3.
 */
import React from "react";
import ReactDOM from "react-dom";
import $ from "jquery";
import { ErrorSummary } from "shared/components/form";
import {
  buildRegistryFromForm,
  focusField,
  type ErrorFieldEntry,
  type StructuredError,
} from "./error-field-registry";

const MOUNT_ID = "event-edit-error-summary";
const SERVER_ERRORS_ID = "event-edit-server-errors";

/**
 * Static mapping from `event.errors_on` keys to structured error metadata.
 * Kept here beside the code that consumes it so changes stay in one file.
 */
const SERVER_ERROR_MAP: Record<string, Omit<StructuredError, "fieldId"> & { fieldId: string }> = {
  name: { fieldId: "event_name", fieldName: "Event Name", message: "You need a name." },
  description: { fieldId: "event_description", fieldName: "Description", message: "You need a description." },
  start_date: { fieldId: "start_date", fieldName: "Start Date", message: "A valid date must be entered." },
  photo: { fieldId: "photo-chooser", fieldName: "Photo", message: "You need to upload a photo." },
  urlname: { fieldId: "event_urlname", fieldName: "Vanity URL", message: "Must contain only letters, numbers, dashes, and underscores." },
};

let registry: Record<string, ErrorFieldEntry> = {};

/**
 * Render (or clear) the ErrorSummary into the mount point.
 * When errors are present, focuses the summary container so screen
 * readers announce it (WCAG 4.1.3 Status Messages).
 */
const renderSummary = (errors: StructuredError[]): void => {
  const mount = document.getElementById(MOUNT_ID);
  if (!mount) return;

  if (!errors.length) {
    ReactDOM.unmountComponentAtNode(mount);
    return;
  }

  const handleErrorClick = (error: StructuredError): void => {
    const entry = registry[error.fieldId];
    if (entry) {
      focusField(entry);
    } else {
      const el = document.getElementById(error.fieldId);
      if (el) {
        el.focus({ preventScroll: false });
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  ReactDOM.render(
    React.createElement(ErrorSummary, {
      errors: errors.map((e) => ({
        fieldId: e.fieldId,
        fieldName: e.fieldName,
        message: e.message,
      })),
      onErrorClick: handleErrorClick,
    }),
    mount,
    () => {
      requestAnimationFrame(() => {
        const summary = mount.querySelector<HTMLElement>(".error-summary");
        if (summary) {
          if (!summary.hasAttribute("tabindex")) {
            summary.setAttribute("tabindex", "-1");
          }
          summary.focus({ preventScroll: false });
          summary.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
  );
};

/**
 * Read server-side validation error keys pre-seeded as JSON in the template.
 * The template outputs `event.errors_on` as a simple JSON array of strings
 * (e.g. `["name","description"]`). We map them to structured errors here.
 */
const readServerErrors = (): StructuredError[] => {
  const script = document.getElementById(SERVER_ERRORS_ID);
  if (!script) return [];

  try {
    const keys: string[] = JSON.parse(script.textContent || "[]");
    if (!Array.isArray(keys)) return [];

    return keys
      .map((key) => SERVER_ERROR_MAP[key])
      .filter((entry): entry is StructuredError => !!entry);
  } catch {
    return [];
  }
};

/**
 * Initialize: build registry, listen for validation events, and
 * render any server-side errors on page load.
 */
export const initErrorSummary = (): void => {
  const form = document.getElementById("new_event_form");
  if (!form) return;

  registry = buildRegistryFromForm(form);

  $(form).on(
    "validation:complete",
    (_event: any, data: { errors: string[]; structuredErrors?: StructuredError[] }) => {
      // Rebuild registry on each validation — fields like photo_key get
      // data-validation attributes added in validation:before, after the
      // initial registry build at page load.
      registry = buildRegistryFromForm(form);

      const clientErrors = data.structuredErrors || [];

      // Check for date instance errors added by EventInstanceManager
      // (it pushes string errors to data.errors but not structuredErrors)
      const hasDateError = data.errors.some(
        (e: string) => typeof e === "string" && e.includes("valid date"),
      );
      if (hasDateError && !clientErrors.some((e) => e.fieldId === "start_date")) {
        const dateError: StructuredError = {
          fieldId: "start_date",
          fieldName: "Start Date",
          message: "A valid date must be entered.",
        };

        // Insert at the correct position so summary order matches field
        // order on the page (Start Date appears after Description).
        const dateEl = document.getElementById("start_date");
        let insertAt = clientErrors.length;
        if (dateEl) {
          for (let i = 0; i < clientErrors.length; i++) {
            const errEl = document.getElementById(clientErrors[i].fieldId);
            if (errEl && dateEl.compareDocumentPosition(errEl) & Node.DOCUMENT_POSITION_FOLLOWING) {
              insertAt = i;
              break;
            }
          }
        }
        clientErrors.splice(insertAt, 0, dateError);
      }

      renderSummary(clientErrors);
    },
  );

  const serverErrors = readServerErrors();
  if (serverErrors.length > 0) {
    renderSummary(serverErrors);
  }
};
